import React, {FC} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {ReplyLocationsEnum} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../../srcCommon/permissions/permissionDenied';
import {useComposeMessageOfflinePermission} from '../../../../../../../srcCommon/permissions/useOfflinePermission';
import {PermissionResult} from '../../../../../../helpers/permissionHelpers';
import {MacroTriggersContainer} from '../../../macroTriggers/macroTriggersContainer';
import {MessageViewerActionReply} from '../action/messageViewerActionReply';
import {MessageViewerActionZone} from '../action/messageViewerActionZone';

/*
 * Props.
 */

interface MessageViewerEmailActionsProps {
  canReply: boolean;
  canReplyAll: boolean;
  hasMacroTrigger: boolean;
  onReplyClick: (arg: ReplyLocationsEnum) => void;
  onReplyAllClick: (arg: ReplyLocationsEnum) => void;
  namespace: string;
  message: MessageModel;
  activityId?: number;
  isInMessagePopup: boolean;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
}

/*
 * Component.
 */

export const MessageViewerEmailActions: FC<MessageViewerEmailActionsProps> = ({
  canReply,
  canReplyAll,
  hasMacroTrigger,
  onReplyClick,
  onReplyAllClick,
  isInMessagePopup,
  message,
  activityId,
  panelId,
}) => {
  const offlinePermission = useComposeMessageOfflinePermission();

  const makeOnReplyClickWrapper = (isReplyAll: boolean) => (isReplyAll ? onReplyAllClick : onReplyClick);
  const hasActions = Boolean(canReply || canReplyAll || (hasMacroTrigger && activityId));

  if (!hasActions) {
    return null;
  }

  return (
    <PermissionCheck offlinePermission={offlinePermission}>
      {(permission) => (
        <MessageViewerActionZone isInPopup={isInMessagePopup}>
          {canReply && (
            <TooltipCoordinator render={() => renderPermissionDeniedTooltip(permission)}>
              <MessageViewerActionReply
                iconName="reply"
                onClick={makeOnReplyClickWrapper(false)}
                messageType={message.type}
                isDisabled={!permission.isAllowed}
              />
            </TooltipCoordinator>
          )}
          {canReplyAll && (
            <TooltipCoordinator render={() => renderPermissionDeniedTooltip(permission)}>
              <MessageViewerActionReply
                iconName="replyAll"
                onClick={makeOnReplyClickWrapper(true)}
                messageType={message.type}
                textOverride={<Trans id="message-replyall">Reply all</Trans>}
                isDisabled={!permission.isAllowed}
              />
            </TooltipCoordinator>
          )}
          {hasMacroTrigger && <MacroTriggersContainer message={message} panelId={panelId} />}
        </MessageViewerActionZone>
      )}
    </PermissionCheck>
  );
};

function renderPermissionDeniedTooltip(permissionResult: PermissionResult) {
  if (permissionResult.isAllowed) {
    return null;
  }

  return <PermissionDenied reason={permissionResult.reason} />;
}
